import { useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { Bell, WifiOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const POLL_MS = 30 * 1000;
const SEEN_KEY = 'rh_notif_seen_at';

function timeAgo(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export default function NotificationsBell({ role }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [unseen, setUnseen] = useState(0);
  const wrapRef = useRef(null);
  const nav = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get('/notifications/offline');
      setItems(Array.isArray(data) ? data : []);
      const seenAt = localStorage.getItem(SEEN_KEY) || '';
      const count = (data || []).filter(x => (x.created_at || '') > seenAt).length;
      setUnseen(count);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!['admin', 'manager'].includes(role)) return;
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [role]);

  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) {
      localStorage.setItem(SEEN_KEY, new Date().toISOString());
      setUnseen(0);
    }
  };

  if (!['admin', 'manager'].includes(role)) return null;

  return (
    <div className="relative ml-auto" ref={wrapRef}>
      <button
        onClick={toggle}
        data-testid="notif-bell"
        className="relative p-2 rounded-md hover:bg-white/5 border border-transparent hover:border-border transition-colors"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5 text-muted-foreground" />
        {unseen > 0 && (
          <span
            data-testid="notif-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-black grid place-items-center"
          >
            {unseen > 9 ? '9+' : unseen}
          </span>
        )}
      </button>
      {open && (
        <div
          data-testid="notif-panel"
          className="absolute right-0 mt-2 w-[360px] max-h-[440px] overflow-hidden rounded-lg border border-border bg-card shadow-xl z-50"
        >
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Live alerts</div>
              <div className="font-display text-sm font-black">Team offline &gt; 10m</div>
            </div>
            {role === 'admin' && (
              <button
                onClick={() => { setOpen(false); nav('/offline-log'); }}
                data-testid="notif-view-all"
                className="text-[11px] uppercase tracking-widest text-primary hover:underline"
              >
                View all
              </button>
            )}
          </div>
          <div className="max-h-[380px] overflow-y-auto divide-y divide-border">
            {items.length === 0 && (
              <div className="p-6 text-sm text-muted-foreground text-center">No offline alerts in the last 24h. Everyone's shining.</div>
            )}
            {items.map(it => (
              <div key={it.id} className="p-3 flex items-start gap-3 hover:bg-accent/40">
                <div className={`w-8 h-8 rounded-md grid place-items-center ${it.came_online_at ? 'bg-emerald-500/15 text-emerald-500' : 'bg-red-500/15 text-red-500'}`}>
                  <WifiOff className="w-4 h-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{it.user_name} <span className="text-[10px] uppercase tracking-widest text-muted-foreground ml-1">{it.role}</span></div>
                  <div className="text-xs text-muted-foreground">
                    {it.came_online_at
                      ? <>Was offline · {it.duration_minutes ?? '—'} min · returned {timeAgo(it.came_online_at)}</>
                      : <>Offline since {timeAgo(it.went_offline_at)} · still away</>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
